import { motion } from "framer-motion";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";

interface BubblesProps {
  count?: number;
  className?: string;
}

const SEEDED = (i: number, n: number) => {
  const x = ((i * 37) % 100);
  const size = 4 + ((i * 13) % n);
  const duration = 5 + ((i * 7) % 6);
  const delay = (i * 0.6) % 5;
  return { x, size, duration, delay };
};

/**
 * Ambient bubbles drifting upward, for ocean-toned sections — this is the
 * site's existing ambient-particle background effect, deployed across every
 * page's hero. Self-gates on reduced motion rather than requiring every one
 * of its 14+ call sites to wrap it individually: only Home.tsx's hero
 * happened to do that already, every other page's Bubbles ran its infinite
 * repeat animation regardless of the visitor's OS preference (ZP-92's own
 * AC: "prefers-reduced-motion... disables parallax and particle effects in
 * favor of static backgrounds").
 */
export default function Bubbles({ count = 14, className = "" }: BubblesProps) {
  const reducedMotion = usePrefersReducedMotion();
  if (reducedMotion) return null;

  return (
    <div className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}>
      {Array.from({ length: count }).map((_, i) => {
        const { x, size, duration, delay } = SEEDED(i, 10);
        return (
          <motion.span
            key={i}
            className="absolute rounded-full bg-teal-200/20 ring-1 ring-teal-100/20"
            style={{ left: `${x}%`, width: size, height: size, bottom: "-5%" }}
            initial={{ y: 0, opacity: 0 }}
            animate={{ y: "-120vh", opacity: [0, 0.8, 0.8, 0] }}
            transition={{ duration, delay, repeat: Infinity, ease: "linear" }}
          />
        );
      })}
    </div>
  );
}
